import React from "react";
import { useTranslation } from "react-i18next";
import { Cart } from "../../types/PricingCalculator";

interface DeviceStrategyStepProps {
  cart: Cart;
  onUpdate: (updates: Partial<Cart>) => void;
}

const DeviceStrategyStep: React.FC<DeviceStrategyStepProps> = ({
  cart,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const handleSmartphoneChoice = (existingSmartphone: boolean) => {
    onUpdate({
      strategy: {
        existingSmartphone,
        phone: existingSmartphone
          ? {
              port: undefined,
              otg: undefined,
              bluetooth: undefined,
              os: undefined,
            }
          : undefined,
      },
    });
  };

  const handlePhoneDetailsChange = (field: string, value: any) => {
    onUpdate({
      strategy: {
        ...cart.strategy,
        phone: {
          ...cart.strategy.phone,
          [field]: value,
        },
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-300">
          {t("pricingCalc.deviceStep.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">{t("pricingCalc.deviceStep.subtitle")}</p>
      </div>

      {/* Smartphone Choice */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-300">
          {t("pricingCalc.deviceStep.qExistingPhone")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => handleSmartphoneChoice(true)}
            className={`p-6 border-2 rounded-lg text-left transition-colors ${
              cart.strategy.existingSmartphone === true
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <div className="flex items-center mb-2">
              <input
                type="radio"
                checked={cart.strategy.existingSmartphone === true}
                onChange={() => handleSmartphoneChoice(true)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-3 text-lg font-medium">
                {t("pricingCalc.deviceStep.yesUsePhone")}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              {t("pricingCalc.deviceStep.yesUsePhoneDesc")}
            </p>
          </button>

          <button
            onClick={() => handleSmartphoneChoice(false)}
            className={`p-6 border-2 rounded-lg text-left transition-colors ${
              cart.strategy.existingSmartphone === false
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <div className="flex items-center mb-2">
              <input
                type="radio"
                checked={cart.strategy.existingSmartphone === false}
                onChange={() => handleSmartphoneChoice(false)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-3 text-lg font-medium">
                {t("pricingCalc.deviceStep.noDedicated")}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              {t("pricingCalc.deviceStep.noDedicatedDesc")}
            </p>
          </button>
        </div>
      </div>

      {/* Phone Details (only if using existing smartphone) */}
      {cart.strategy.existingSmartphone && (
        <div className="bg-gray-50 p-6 rounded-lg space-y-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t("pricingCalc.deviceStep.phoneDetailsTitle")}
          </h3>
          <p className="text-sm text-gray-600">
            {t("pricingCalc.deviceStep.phoneDetailsSubtitle")}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("pricingCalc.deviceStep.portLabel")}
              </label>
              <select
                value={cart.strategy.phone?.port || ""}
                onChange={(e) =>
                  handlePhoneDetailsChange("port", e.target.value || undefined)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">
                  {t("pricingCalc.deviceStep.portPlaceholder")}
                </option>
                <option value="usb_c">USB-C</option>
                <option value="micro_usb">Micro USB</option>
                <option value="lightning">Lightning (iPhone)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("pricingCalc.deviceStep.osLabel")}
              </label>
              <select
                value={cart.strategy.phone?.os || ""}
                onChange={(e) =>
                  handlePhoneDetailsChange("os", e.target.value || undefined)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">
                  {t("pricingCalc.deviceStep.osPlaceholder")}
                </option>
                <option value="android">Android</option>
                <option value="ios">iOS (iPhone)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("pricingCalc.deviceStep.otgLabel")}
              </label>
              <select
                value={
                  cart.strategy.phone?.otg === undefined
                    ? ""
                    : cart.strategy.phone.otg.toString()
                }
                onChange={(e) =>
                  handlePhoneDetailsChange(
                    "otg",
                    e.target.value === ""
                      ? undefined
                      : e.target.value === "true"
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t("pricingCalc.deviceStep.unknown")}</option>
                <option value="true">{t("pricingCalc.deviceStep.yes")}</option>
                <option value="false">{t("pricingCalc.deviceStep.no")}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("pricingCalc.deviceStep.btLabel")}
              </label>
              <select
                value={
                  cart.strategy.phone?.bluetooth === undefined
                    ? ""
                    : cart.strategy.phone.bluetooth.toString()
                }
                onChange={(e) =>
                  handlePhoneDetailsChange(
                    "bluetooth",
                    e.target.value === ""
                      ? undefined
                      : e.target.value === "true"
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t("pricingCalc.deviceStep.unknown")}</option>
                <option value="true">{t("pricingCalc.deviceStep.yes")}</option>
                <option value="false">{t("pricingCalc.deviceStep.no")}</option>
              </select>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-800">
                  {t("pricingCalc.deviceStep.tipsTitle")}
                </h4>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>{t("pricingCalc.deviceStep.tipBt")}</li>
                    <li>{t("pricingCalc.deviceStep.tipUsb")}</li>
                    <li>{t("pricingCalc.deviceStep.tipAuto")}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceStrategyStep;
